import { PageController } from '@client/components/_core/layout/PageController';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Tag, Typography } from 'antd';
import { DateTime } from 'luxon';
import { useState } from 'react';

const { Text } = Typography;

export const Route = createFileRoute('/console/$orgId/configuration/financials/billing/transactions')({
    component: Transactions,
});

type Transaction = RouterOutputs['billing']['listWalletTransactions']['rows'][number];
type OrderBy = NonNullable<RouterInputs['billing']['listWalletTransactions']['orderBy']>;

const columnHelper = createColumnHelper<Transaction, OrderBy>();

function Transactions() {
    const orgId = useOrgId();
    const { trpc } = useTrpc();
    const [queryParams, setQueryParams] = useState<RouterInputs['billing']['listWalletTransactions']>({
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        orderBy: { id: 'desc' },
        where: { orgId },
    });
    const { fDateTimeLxn } = useFormatter();

    // Setup transactions query
    const { data: transactionsData, isLoading: transactionsLoading } =
        trpc.billing.listWalletTransactions.useQuery(queryParams);

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ value }) => <Text>{String(value)}</Text>,
        }),
        columnHelper.accessor('serviceCode', {
            id: 'serviceCode',
            header: 'Code',
            sortable: 'serviceCode',
            cell: ({ value }) => (
                <Tag variant="outlined" color="blue">
                    {value}
                </Tag>
            ),
        }),
        columnHelper.accessor('amount', {
            id: 'amount',
            header: 'Amount',
            sortable: 'amount',
            cell: ({ value }) => (
                <Text strong type={value.isNegative() ? 'danger' : 'success'}>
                    {value.isNegative() ? '-' : '+'}US${Math.abs(Number(value)).toFixed(2)}
                </Text>
            ),
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Date',
            sortable: 'createdAt',
            cell: ({ value }) => <Text type="secondary">{DateTime.fromJSDate(value).toFormat(fDateTimeLxn)}</Text>,
        }),
        columnHelper.accessor('note', {
            id: 'note',
            header: 'Note',
            cell: ({ value }) => <Text>{value || 'N/A'}</Text>,
        }),
    ];

    const tableProps = usePrismaTable({
        data: transactionsData?.rows ?? [],
        total: transactionsData?.total ?? 0,
        loading: transactionsLoading,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'bottom',
            showSizeChanger: true,
        },
        scroll: { x: 500 },
    });

    return (
        <PageController route={Route}>
            <PrismaTable {...tableProps} className="shadow-xs" />
        </PageController>
    );
}
